// ESC/POS 指令拼装（纯字节，不依赖第三方打印机库）
// 中文用 GBK 编码（得力等国产热敏打印机内置字库默认 GBK）
import iconv from 'iconv-lite';
import { DEFAULT_COL_WIDTHS } from './receiptSettings';

function pushText(bytes, s) {
  const buf = iconv.encode(String(s), 'gbk');
  for (let i = 0; i < buf.length; i++) bytes.push(buf[i]);
}

// 显示宽度：ASCII = 1，中文 = 2
function displayWidth(s) {
  let w = 0;
  for (const ch of String(s)) {
    // eslint-disable-next-line no-control-regex
    w += /[\x00-\x7f]/.test(ch) ? 1 : 2;
  }
  return w;
}

/**
 * 清洗商品名：
 *  - 去掉括号及其内容（如 "公中 (香烟)" → "公中"）
 *  - 去掉前后缀及中间多余的 "-" 符号（如 "- 公中 -" → "公中"）
 *  - 压缩多余空白
 */
export function cleanProductName(name) {
  let s = String(name || '');
  // 去掉括号及其内容
  s = s.replace(/\s*[（(][^）)]*[）)]\s*/g, ' ');
  // 去掉 "-" 符号（前后或中间的连字符）
  s = s.replace(/-/g, ' ');
  // 压缩多余空白并去除首尾空格
  return s.trim().replace(/\s+/g, '');
}

/**
 * 按列宽截断商品名（按显示宽度算）
 */
function clipName(name, width) {
  const s = String(name || '');
  if (displayWidth(s) <= width) return s;
  let out = '';
  let w = 0;
  const target = width - 1;
  for (const ch of s) {
    // eslint-disable-next-line no-control-regex
    const cw = /[\x00-\x7f]/.test(ch) ? 1 : 2;
    if (w + cw > target) break;
    out += ch;
    w += cw;
  }
  return out + '…';
}

/**
 * 生成小票的 ESC/POS 字节流
 * @param {Object} opts
 *   storeName    店名（标题，居中加大加粗）
 *   type         'sale' | 'purchase'
 *   typeLabel    类型标题（如 "销售单"/"出货单"，默认按 type 推断）
 *   date         日期字符串
 *   items        [{ name, quantity, price, subtotal }]
 *   total        总计金额（可选，缺省按 items 累加）
 *   totalQuantity 数量（所有商品总件数；可选，缺省自动累加）
 *   cols         { name, qty, price, sub } 列宽（可选）
 *   footer       页脚（默认 谢谢惠顾）
 *   remark       备注（有则打印，无则留空）
 */
export function buildReceiptBytes({
  storeName, type, typeLabel, date, items,
  total, totalQuantity, cols, footer, remark
}) {
  const C = { ...DEFAULT_COL_WIDTHS, ...(cols || {}) };
  const totalW = C.name + C.qty + C.price + C.sub + 4; // +4 为 3 个分隔空格 + 1 安全
  const SEP = '-'.repeat(totalW);
  const b = [];
  const cmd = (...xs) => b.push(...xs);

  // 初始化 + 进入中文模式
  cmd(0x1b, 0x40);
  cmd(0x1c, 0x26);

  // 标题：店名
  cmd(0x1b, 0x61, 0x01);
  cmd(0x1b, 0x45, 0x01);
  cmd(0x1d, 0x21, 0x11);
  pushText(b, (storeName || '店铺名') + '\n');
  cmd(0x1d, 0x21, 0x00);
  cmd(0x1b, 0x45, 0x00);

  // 单据类型（用户自定义标题）
  const label = typeLabel || (type === 'purchase' ? '进货单' : '销售单');
  pushText(b, `── ${label} ──\n\n`);

  // 商品明细标题
  cmd(0x1b, 0x61, 0x01);
  pushText(b, '商品明细\n');
  cmd(0x1b, 0x61, 0x00);
  pushText(b, SEP + '\n');

  // 商品明细 + 累加汇总（每个商品两行：名称左对齐，下一行右对齐显示 数量*单价=金额）
  let qtySum = 0;
  let subSum = 0;
  (items || []).forEach(it => {
    const cleanName = cleanProductName(it.name);
    const name = clipName(cleanName, totalW);
    const qty = Number(it.quantity) || 0;
    const price = Number(it.price) || 0;
    const sub = Number(it.subtotal) || qty * price;
    qtySum += qty;
    subSum += sub;
    // 第一行：商品名（左对齐，只显示商品名称，不含单价/数量/金额）
    pushText(b, name + '\n');
    // 第二行：数量 × 单价 = 金额（左对齐，缩进两个全角空格）
    pushText(b, `  ${qty} × ${price.toFixed(1)} = ${sub.toFixed(1)}\n`);
  });

  const finalQty = (Number(totalQuantity) || 0) || qtySum;
  const finalTotal = (Number(total) || 0) || subSum;

  pushText(b, SEP + '\n');

  // 汇总（右对齐 + 加粗）
  cmd(0x1b, 0x61, 0x02);
  cmd(0x1b, 0x45, 0x01);
  pushText(b, `数量: ${finalQty}\n`);
  pushText(b, `总计: ${finalTotal.toFixed(1)} 元\n`);
  cmd(0x1b, 0x45, 0x00);
  cmd(0x1b, 0x61, 0x00);

  // 备注（有则显示，居左）
  if (typeof remark !== 'undefined') {
    const r = String(remark || '').trim();
    if (r) pushText(b, '\n备注：' + r + '\n');
    else pushText(b, '\n备注：\n');
  }

  // 日期 + 页脚（居中）
  cmd(0x1b, 0x61, 0x01);
  if (date) pushText(b, '\n' + String(date) + '\n');
  pushText(b, '\n' + (footer || '谢谢惠顾') + '\n\n\n');

  // 切纸
  cmd(0x1d, 0x56, 0x42, 0x00);

  return new Uint8Array(b);
}

/**
 * 预览用结构化数据
 */
export function buildReceiptPreview(opts) {
  const C = { ...DEFAULT_COL_WIDTHS, ...(opts.cols || {}) };
  const totalW = C.name + C.qty + C.price + C.sub + 4;

  const items = (opts.items || []).map(it => {
    const qty = Number(it.quantity) || 0;
    const price = Number(it.price) || 0;
    const sub = Number(it.subtotal) || qty * price;
    const cleanName = cleanProductName(it.name);
    return {
      name: clipName(cleanName, totalW),
      nameFull: cleanName,
      quantity: qty,
      price: price.toFixed(1),
      subtotal: sub.toFixed(1),
      line: `  ${qty} × ${price.toFixed(1)} = ${sub.toFixed(1)}`
    };
  });

  const qtySum = items.reduce((a, x) => a + x.quantity, 0);
  const subSum = items.reduce((a, x) => a + Number(x.subtotal), 0);

  return {
    storeName: opts.storeName || '店铺名',
    type: opts.type || 'sale',
    typeLabel: opts.typeLabel || (opts.type === 'purchase' ? '进货单' : '销售单'),
    date: opts.date || '',
    items,
    total: ((Number(opts.total) || 0) || subSum).toFixed(1),
    totalQuantity: ((Number(opts.totalQuantity) || 0) || qtySum),
    footer: opts.footer || '谢谢惠顾',
    sep: '-'.repeat(totalW),
    cols: C,
    totalWidth: totalW
  };
}

/**
 * 测试打印字节流
 */
export function buildTestBytes() {
  return buildReceiptBytes({
    storeName: '打印测试',
    type: 'sale',
    date: new Date().toLocaleString('zh-CN'),
    items: [
      { name: '可口可乐', quantity: 2, price: 3.0 },
      { name: '雪碧', quantity: 1, price: 3.5 },
      { name: '百事可乐（特价）', quantity: 3, price: 6.5 }
    ],
    total: 9.5,
    totalQuantity: 6,
    footer: '谢谢惠顾'
  });
}
